import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { List, ListIteratee, remove, pull } from 'lodash'
import { SadukaFile, SadukaTempFileUploadResponse } from 'cityads-v3/types/file';
import { uploadFile } from 'cityads-v3/actions/file';
import {
  useNotEmptyValidator,
  useFieldValidators,
  useDomainNameValidator,
  useSubdomainNameValidator,
  useUrlValidator,
  useEmailAddressValidator,
  useOnlyDigitsValidator,
  useLessOrEqualValidator,
  ValidatorName,
  FileValidatorName,
  ValidatorsParams,
} from './validators';
import { ensureArray } from 'cityads-v3/utils/arrayUtils';

class Wrapper<T> {
  // wrapped has no explicit return type so we can infer it
  wrapped(
    validators?: ValidatorName[],
    validatorsParams?: ValidatorsParams,
    defaultValue?: T
  ) {
    return useFormInput<T>(validators, validatorsParams, defaultValue)
  }
}

export type useFormInputType<T = unknown> = ReturnType<Wrapper<T>['wrapped']>

/**
 * @deprecated
 * use @xvii/useform
 */
export function useFormInput<T = string>(
  validators?: ValidatorName[],
  validatorsParams?: ValidatorsParams,
  defaultValue?: T
) {
  const [value, setValue] = useState<T>(defaultValue);
  const [error, setError] = useState<string | undefined>();
  const [usedValidators, setUsedValidators] = useState<ValidatorName[]>(validators)

  const isValidationRequired = useRef(false);

  const notEmpty = useNotEmptyValidator(usedValidators?.includes('notEmpty') ?? false, value);
  const domainName = useDomainNameValidator(usedValidators?.includes('domainName') ?? false, String(value));
  const subdomainName = useSubdomainNameValidator(usedValidators?.includes('subdomainName') ?? false, String(value));
  const url = useUrlValidator(usedValidators?.includes('url') ?? false, String(value));
  const emailAddress = useEmailAddressValidator(usedValidators?.includes('emailAddress') ?? false, String(value));
  const onlyDigits = useOnlyDigitsValidator(usedValidators?.includes('onlyDigits') ?? false, String(value), validatorsParams?.['onlyDigits']);
  const lessOrEqualTo = useLessOrEqualValidator(usedValidators?.includes('lessOrEqualTo') ?? false, String(value), validatorsParams?.['lessOrEqualTo']);
  const memoizedValidators = useMemo(() => [
    notEmpty,
    domainName,
    subdomainName,
    url,
    emailAddress,
    onlyDigits,
    lessOrEqualTo,
  ], [
    notEmpty,
    domainName,
    subdomainName,
    url,
    emailAddress,
    onlyDigits,
    lessOrEqualTo,
  ])
  const validate = useFieldValidators(memoizedValidators, setError)

  const handleValueChange = useCallback((newValue: T) => {
    if (error) {
      isValidationRequired.current = true
    }

    setValue(newValue)
  }, [error, setValue])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue: string | number | boolean = event.target.value
    if (typeof defaultValue === 'number') {
      newValue = Number(newValue);
    }

    handleValueChange(newValue as unknown as T)
    return newValue as unknown as T
  }, [handleValueChange, defaultValue])

  const pushToArray = useCallback((setValue: ListIteratee<T> | number | string) => {
    if (!Array.isArray(value)) {
      console.error('Value is not an array');
      return;
    }

    const newValue = [
      ...value,
      ...ensureArray(setValue)
    ];

    handleValueChange(newValue as unknown as T)
  }, [value, handleValueChange])

  const deleteFromArray = useCallback((predicate: ListIteratee<T> | number | string) => {
    if (!Array.isArray(value)) {
      console.error('Value is not an array');
      return;
    }

    const newValue = [...value];
    if (typeof predicate === 'object' && !Array.isArray(predicate)) {
      remove(newValue as List<T>, predicate)
    } else {
      pull(newValue, ...ensureArray(predicate))
    }
    handleValueChange(newValue as unknown as T)
  }, [value, handleValueChange])


  useEffect(() => {
    if (isValidationRequired.current) {
      validate()
      isValidationRequired.current = false
    }
  }, [value, validate])

  const reset = useCallback(() => {
    setValue(defaultValue)
    setError(undefined)
  }, [])

  return {
    value,
    handleValueChange,
    handleChange,
    error,
    pushToArray,
    deleteFromArray,
    setError,
    setValidators: setUsedValidators,
    setValue,
    validate,
    reset,
  }
}

export function useFormFile(validators?: FileValidatorName[]) {
  const dispatch = useDispatch()
  const fileForm = useFormInput<SadukaFile>(validators, {}, null)
  const [nativeFile, setNativeFile] = useState<File | null>(null)

  const handleFileChange = useCallback((newFiles: File[]) => {
    if (!newFiles.length) {
      fileForm.reset()
      setNativeFile(null)
    } else {
      const newFile = newFiles[0];

      setNativeFile(newFile)
      const act = async () => {
        const response: SadukaTempFileUploadResponse = await dispatch(uploadFile(newFile))

        fileForm.setValue({
          fileName: newFile.name,
          isNew: true,
          tempId: response.url,
        })
      }

      act()
    }
  }, [setNativeFile, fileForm.setValue, fileForm.reset])

  return {
    ...fileForm,
    fileValue: nativeFile ?? fileForm.value,
    handleFileChange,
  }
}

export function useFormCheckbox(
  validators?: ValidatorName[],
  validatorsParams?: ValidatorsParams,
  defaultValue?: boolean
) {
  const input = useFormInput<boolean>(validators, validatorsParams, defaultValue ?? false);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    input.setValue(event.target.checked);
  }, [input.setValue])

  const handleToggle = useCallback(() => {
    input.setValue(!input.value);
  }, [input.value, input.setValue])

  return {
    ...input,
    handleChange,
    handleToggle
  }
}

export function useFormState() {
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isSent, setIsSent] = useState<boolean>(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const setFailed = useCallback((error: unknown) => {
    setError(error);
    setIsSent(false);
    setIsSending(false);
  }, [setError, setIsSent, setIsSending]);

  const setSucceeded = useCallback((result: unknown = null) => {
    setIsSent(true);
    setIsSending(false);
    setError(null);
    setResult(result);
  }, []);

  return {
    isSending,
    isSent,
    error,
    result,

    setFailed,
    setSucceeded,
    setIsSending,
    setIsSent,
    setError,
  }
}

export const formInputNumberBlank: useFormInputType<number> = {
  value: null,
  error: '',
  handleChange: v => null,
  handleValueChange: v => { },
  reset: () => { },
  pushToArray: () => { },
  deleteFromArray: () => { },
  setError: () => { },
  setValidators: () => { },
  setValue: () => { },
  validate: () => false
}
